/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IColumn } from '@fluentui/react/lib/DetailsList';
import { ILabelStyleProps, ILabelStyles, Label } from '@fluentui/react/lib/Label';
import { IScrollablePaneProps, IScrollablePaneStyles, ScrollablePane } from '@fluentui/react/lib/ScrollablePane';
import { Stack } from '@fluentui/react/lib/Stack';
import { FontWeights } from '@fluentui/react/lib/Styling';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { Property } from 'csstype';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { CalloutButton } from 'src/core/components/CalloutButton';
import { LuisText } from 'src/core/components/LuisText';
import { Table } from 'src/core/components/Table';
import { TableProps } from 'src/core/components/TableProps';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useSelection } from 'src/core/hooks/useSelection';
import { AutotagTriggerContentLeftMargin } from 'src/customText/verticals/autotagTriggerModal/autotagTriggerModal';
import { AutoLabelingModalFooter } from 'src/customText/verticals/autotagTriggerModal/components/AutoLabelingModalFooter';
import { AutoLabelingInfoDto, AutoLabelingSteps } from 'src/customText/verticals/autotagTriggerModal/types';
import { getAutoLabelingStepStatus } from 'src/customText/verticals/autotagTriggerModal/utils';

type ClassItem = { key: string; name: string; totalTags: number };

export type AutotagTriggerModalSelectClassesProps = TableProps<ClassItem> & {
    onDismiss: () => void;
    onPrevious: () => void;
    autotagInfoDto: AutoLabelingInfoDto;
    onSubmit: (classesNames: string[]) => void;
};

const messages = defineMessages({
    classes: { id: 'customText.selectClassesScreen.classes', defaultMessage: 'Classes' },
    totalTags: { id: 'customText.selectClassesScreen.totalTags', defaultMessage: 'Total labels' },
    title: { id: 'customText.selectClassesScreen.title', defaultMessage: 'Select classes to label' },
    description: {
        id: 'customText.selectClassesScreen.description',
        defaultMessage: `Select which classes you’d like to auto-label. By default, all existing
        classes are included in the auto-labeling process. You must have at least 1 class to auto-label.`
    }
});

const labelsStyles = classNamesFunction<ILabelStyleProps, ILabelStyles>()({
    root: { display: 'flex', selectors: { '::after': { paddingRight: 0 } } }
});
const scrollablePaneStyles = classNamesFunction<IScrollablePaneProps, IScrollablePaneStyles>()({
    root: { minHeight: '400px', height: '100%', position: 'relative' }
});

export const SelectClassesScreen = observer((props: AutotagTriggerModalSelectClassesProps) => {
    const { autotagInfoDto, onSubmit, onDismiss, onPrevious, ...rest } = props;
    const { localize } = useLocalization();
    const { rootStore } = useGlobalState();
    const { selection, selectedItems } = useSelection<ClassItem>();

    const classesTableRef = React.useRef<HTMLDivElement>();

    const classes = rootStore.currentCustomTextProjectStore.classificationStore.classes ?? [];
    const items: ClassItem[] = classes.map(c => ({
        key: c.name,
        name: c.name,
        totalTags: rootStore.currentCustomTextProjectStore.labelsCount.get(c.id) ?? 0
    }));

    const labelOnRender = (label: string, tooltipText: string | React.ReactNode) => (
        <Stack role="menuitem" horizontal verticalAlign="center">
            <Label styles={labelsStyles}>{label}</Label>
            <CalloutButton content={tooltipText} />
        </Stack>
    );

    const columns: IColumn[] = [
        {
            key: 'name',
            minWidth: 50,
            maxWidth: 100,
            isRowHeader: true,
            isResizable: true,
            fieldName: 'name',
            name: localize(messages.classes),
            onRenderHeader: () => labelOnRender(localize(messages.classes), localize(messages.classes))
        },
        {
            minWidth: 150,
            maxWidth: 200,
            key: 'totalTags',
            isResizable: true,
            fieldName: 'totalTags',
            name: localize(messages.totalTags),
            onRenderHeader: () => labelOnRender(localize(messages.totalTags), localize(messages.totalTags))
        }
    ];

    /**
     * The column header cell is a button contains entity name and a callout which is focusable and it's not valid in a11y
     * that a button contains nested interactive elements so this useEffect gets that cell and changes its role to be menu.
     * And the callout button will be a menuitem.
     */
    React.useEffect(() => {
        const columnHeaders = classesTableRef?.current?.getElementsByClassName('ms-DetailsHeader-cellTitle');
        for (const header of columnHeaders) {
            document.getElementById(header.id)?.setAttribute('role', 'menu');
        }
        selection.setAllSelected(true);
    }, []);

    return (
        <Stack>
            <Stack
                styles={{ root: { height: '100px', overflowY: 'auto' } }}
                tokens={{ childrenGap: 24, padding: `0 0 0 ${AutotagTriggerContentLeftMargin}` }}>
                <Stack tokens={{ childrenGap: 6 }}>
                    <LuisText variant="xLarge" fontWeight={FontWeights.semibold as Property.FontWeight}>
                        {localize(messages.title)}
                    </LuisText>
                    <LuisText>{localize(messages.description)}</LuisText>
                </Stack>
            </Stack>
            <Stack.Item grow tokens={{ padding: `0 0 0 ${AutotagTriggerContentLeftMargin}` }}>
                <ScrollablePane styles={scrollablePaneStyles}>
                    <div ref={classesTableRef}>
                        <Table<ClassItem> items={items} columns={columns} selection={selection} {...rest} />
                    </div>
                </ScrollablePane>
            </Stack.Item>
            <AutoLabelingModalFooter
                onDismiss={onDismiss}
                onPrevious={onPrevious}
                activeStep={AutoLabelingSteps.SelectEntitiesOrClasses}
                onNext={() => onSubmit(selectedItems.map(sI => sI.name))}
                isValid={
                    getAutoLabelingStepStatus(
                        AutoLabelingSteps.SelectEntitiesOrClasses,
                        AutoLabelingSteps.SelectEntitiesOrClasses,
                        { ...autotagInfoDto, classesNames: selectedItems.map(e => e.name) },
                        rootStore
                    ).isValid
                }
            />
        </Stack>
    );
});
